import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";

import useAuth from "../../hooks/useAuth";
import useMutation from "../../hooks/useMutation";
import useWizardStep from "../../hooks/useWizardStep";
import useQueryParams from "../../hooks/useQueryParams";
import { getShipmentValues } from "../../utils/shipment";
import { NOTIFY_CUSTOMER_SHIPMENT_MUTATION } from "../../constants/graphql/mutations";

// Import existing step components
import Shipment from "../../components/capture/ReceivingWizard/Shipment/Shipment";
import Items from "../../components/capture/ReceivingWizard/Items/Items";
import AdditionalCharges from "../../components/capture/ReceivingWizard/AdditionalCharges/AdditionalCharges";
import Reservations from "../../components/capture/ReceivingWizard/Reservations/Reservations";
import OSAndD from "../../components/capture/ReceivingWizard/OSAndD/OSAndD";
import IntroStep from "./IntroStep";
import BillingIntroStep from "./BillingIntroStep";
import useReceivingWizardFlow from "../../components/capture/ReceivingWizard/hooks/useReceivingWizardFlow";

// Section Header Component
const SectionHeader = ({
  title,
  isExpanded,
  isComplete,
  badge,
  isActive,
  onToggle,
  isClickable,
}) => (
  <Box
    onClick={isClickable ? onToggle : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 2,
      px: 2,
      cursor: isClickable ? "pointer" : "default",
      bgcolor: isActive ? "#e3f2fd" : "background.paper",
      color: isActive ? "primary.main" : "inherit",
      borderBottom: 1,
      borderColor: "divider",
      borderLeft: isActive ? 3 : 0,
      borderLeftColor: "primary.main",
      "&:hover": isClickable ? { bgcolor: "#f5f5f5" } : {},
      position: "sticky",
      top: 0,
      zIndex: 10,
      transition: "all 0.2s",
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5}>
      {isComplete && <CheckCircleIcon color="success" fontSize="small" />}
      <Typography fontWeight={600} fontSize={15}>
        {title}
      </Typography>
      {badge}
    </Box>
    <IconButton
      size="small"
      disabled={!isClickable}
      sx={{ color: isActive ? "primary.main" : "inherit" }}
    >
      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </IconButton>
  </Box>
);

export default function VerticalReceivingWizard({ shipment, project, onClose, type }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { enqueueSnackbar } = useSnackbar();
  const [notifyCustomer] = useMutation(NOTIFY_CUSTOMER_SHIPMENT_MUTATION);
  const [approving, setApproving] = useState(false);
  const { hasAdditionalCharges, hasInboundReservations } = useReceivingWizardFlow(project);

  const shipmentFormikRef = useRef(null);
  const additionalChargesFormikRef = useRef(null);

  const params = useQueryParams();
  const navigate = useNavigate();

  // Determine steps based on project configuration (excluding intro)
  const workflowSteps = useMemo(() => {
    const baseSteps = [
      { key: "shipment", component: Shipment },
      { key: "items", component: Items },
    ];

    if (hasAdditionalCharges) {
      baseSteps.push({ key: "additional-charges", component: AdditionalCharges });
    }

    if (hasInboundReservations) {
      baseSteps.push({ key: "reservations", component: Reservations });
    }

    baseSteps.push({ key: "os&d", component: OSAndD });

    // Number steps starting from 1
    return baseSteps.map((step, index) => ({
      ...step,
      title: `Step ${index + 1}`,
      stepNumber: index + 1,
    }));
  }, [hasAdditionalCharges, hasInboundReservations]);

  const currentStepParam = useWizardStep(["intro", ...workflowSteps.map(s => s.key)], {
    fallback: "intro",
    skip: !project?.id,
  });
  const isIntroStep = currentStepParam === "intro";
  const isBillingSource = params.get("source") === "billing";
  const currentStepIndex = workflowSteps.findIndex(s => s.key === currentStepParam);
  const activeStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;

  const handleStartReviewing = () => {
    if (params.get("source") === "billing") {
      handleToggleSection("items");
    } else {
      handleToggleSection("shipment");
    }
  };

  const handleToggleSection = stepKey => {
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("step", stepKey);
    navigate({ search: newParams.toString() });
  };

  const handleApprove = async () => {
    setApproving(true);
    notifyCustomer({
      variables: { shipmentId: shipment.id },
      onSuccess: () => {
        enqueueSnackbar(`${settings.filter_field2} notification sent successfully!`, {
          variant: "success",
        });
        setApproving(false);
        onClose();
      },
      onFailure: errors => {
        enqueueSnackbar(errors.submit || `Failed to send ${settings.filter_field2} notification`, {
          variant: "error",
        });
        setApproving(false);
      },
    });
  };

  const initialValues = useMemo(() => getShipmentValues(shipment), [shipment]);

  // No-op close handler for embedded components that expect it
  const handleStepClose = () => {
    // Do nothing - we control navigation via sections
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Show intro step as standalone view */}
      {isIntroStep ? (
        isBillingSource ? (
          <BillingIntroStep
            onStart={handleStartReviewing}
            onApprove={handleApprove}
            isApproving={approving}
          />
        ) : (
          <IntroStep 
            onStart={handleStartReviewing} 
            onApprove={handleApprove}
            isApproving={approving}
          />
        )
      ) : (
        /* Scrollable stepped workflow */
        <Box flexGrow={1} overflow="auto">
          {workflowSteps.map((step, index) => {
            const StepComponent = step.component;
            const isExpanded = index === activeStepIndex;
            // Only mark as complete if user has moved past this step
            const isComplete = index < activeStepIndex;
            const isActive = index === activeStepIndex;

            // Prepare badge content
            let badge = null;
            if (step.key === "shipment" && shipment.draft) {
              badge = <Chip size="small" label="Draft" color="warning" variant="outlined" />;
            } else if (step.key === "items" && shipment.shipmentItems) {
              badge = (
                <Chip
                  size="small"
                  label={`${shipment.shipmentItems.length} item${
                    shipment.shipmentItems.length !== 1 ? "s" : ""
                  }`}
                  variant="outlined"
                />
              );
            }

            return (
              <Box key={step.key}>
                <SectionHeader
                  title={step.title}
                  isExpanded={isExpanded}
                  isComplete={isComplete}
                  isActive={isActive}
                  badge={badge}
                  onToggle={() => handleToggleSection(step.key)}
                  isClickable={index < activeStepIndex}
                />
                <Collapse in={isExpanded}>
                  <Box>
                    {step.key === "shipment" && (
                      <Shipment
                        shipment={shipment}
                        project={project}
                        initialValues={initialValues}
                        onClose={handleStepClose}
                        formikRef={shipmentFormikRef}
                        verticalLayout={true}
                      />
                    )}
                    {step.key === "items" && (
                      <Items
                        shipment={shipment}
                        project={project}
                        onClose={handleStepClose}
                        verticalLayout={true}
                      />
                    )}
                    {step.key === "additional-charges" && (
                      <AdditionalCharges
                        project={project}
                        shipment={shipment}
                        onClose={handleStepClose}
                        formikRef={additionalChargesFormikRef}
                        verticalLayout={true}
                      />
                    )}
                    {step.key === "reservations" && (
                      <Reservations
                        shipment={shipment}
                        project={project}
                        onClose={handleStepClose}
                        verticalLayout={true}
                      />
                    )}
                    {step.key === "os&d" && (
                      <OSAndD
                        type={type}
                        shipment={shipment}
                        project={project}
                        onClose={onClose}
                        verticalLayout={true}
                      />
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
